import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { api } from '../../services/api';
import ImageUploader from '../../components/ImageUploader';
import ProgressBar from '../../components/ProgressBar';
import { Input, TextArea } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import { getImageUrl } from '../../utils/imageUtils';

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
  label { display: block; margin-bottom: 0.5rem; font-weight:500; }
`;

const HeaderRow = styled.div`
  display: flex; justify-content: space-between; align-items: center; margin: 2rem 0 1rem;
`;

const OutreachAdmin = () => {
  const [header, setHeader] = useState({ title: '', description: '', image: '' });
  const [info, setInfo] = useState({ title: '', description: '', image: '' });
  const [posts, setPosts] = useState([]);
  const [sections, setSections] = useState([]);
  const [view, setView] = useState('list');
  const [currentPost, setCurrentPost] = useState(null);
  const [postMsg, setPostMsg] = useState('');
  const [currentSection, setCurrentSection] = useState(null);
  const [sectionView, setSectionView] = useState('list');
  const [currentSectionPost, setCurrentSectionPost] = useState(null);
  const [sectionPostMsg, setSectionPostMsg] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [progress, setProgress] = useState({ header: 0, info: 0, post: 0, sectionImg: 0, sectionPost: 0 });

  const loadData = useCallback(async () => {
    try {
      const [h, i, p, s] = await Promise.all([
        api.get('/outreach/header'),
        api.get('/outreach/info'),
        api.get('/outreach/posts'),
        api.get('/outreach/sections')
      ]);
      setHeader(h);
      setInfo(i);
      setPosts(p);
      setSections(s);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const saveHeader = async () => {
    setSaving(true);
    try {
      await api.put('/outreach/header', { title: header.title, description: header.description });
      setMsg('Header saved');
    } catch (err) { setMsg('Error saving header'); }
    finally { setSaving(false); }
  };

  const saveInfo = async () => {
    setSaving(true);
    try {
      await api.put('/outreach/info', { title: info.title, description: info.description });
      setMsg('Info saved');
    } catch (err) { setMsg('Error saving info'); }
    finally { setSaving(false); }
  };

  const handleHeaderImage = async (file) => {
    if (!file) return;
    setProgress(prev => ({ ...prev, header: 0 }));
    
    const timer = setInterval(() => {
        setProgress(prev => {
            if(prev.header < 90) return { ...prev, header: prev.header + 10 };
            return prev;
        });
    }, 100);

    try {
        const fd = new FormData(); fd.append('image', file);
        const res = await api.upload('/outreach/header/image', fd);
        
        clearInterval(timer);
        setProgress(prev => ({ ...prev, header: 100 }));
        
        setHeader(res);
        setTimeout(() => setProgress(prev => ({ ...prev, header: 0 })), 500);
    } catch (err) {
        clearInterval(timer);
        setProgress(prev => ({ ...prev, header: 0 }));
        console.error(err);
    }
  };

  const handleInfoImage = async (file) => {
    if (!file) return;
    setProgress(prev => ({ ...prev, info: 0 }));
    
    const timer = setInterval(() => {
        setProgress(prev => {
            if(prev.info < 90) return { ...prev, info: prev.info + 10 };
            return prev;
        });
    }, 100);

    try {
        const fd = new FormData(); fd.append('image', file);
        const res = await api.upload('/outreach/info/image', fd);
        
        clearInterval(timer);
        setProgress(prev => ({ ...prev, info: 100 }));
        
        setInfo(res);
        setTimeout(() => setProgress(prev => ({ ...prev, info: 0 })), 500);
    } catch (err) {
        clearInterval(timer);
        setProgress(prev => ({ ...prev, info: 0 }));
        console.error(err);
    }
  };

  const handlePostCreate = async () => {
    setSaving(true); setPostMsg('');
    try {
      const created = await api.post('/outreach/posts', { title: currentPost.title, description: currentPost.description });
      setCurrentPost(created);
      setPostMsg('Post created. You can now add images.');
    } catch (err) { setPostMsg(err.message || 'Error creating post.'); }
    finally { setSaving(false); }
  };

  const handlePostSave = async () => {
    setSaving(true); setPostMsg('');
    try {
      await api.put(`/outreach/posts/${currentPost._id}`, { title: currentPost.title, description: currentPost.description });
      setView('list'); loadData();
    } catch (err) { setPostMsg(err.message || 'Error saving post.'); }
    finally { setSaving(false); }
  };

  const deletePost = async (id) => {
    if (!window.confirm('Delete?')) return;
    await api.delete(`/outreach/posts/${id}`);
    loadData();
  };

  const handlePostImage = async (files) => {
    if (!files) return;
    const fileList = Array.isArray(files) ? files : [files];
    let updatedPost = { ...currentPost };
    setPostMsg('');
    for (const file of fileList) {
      setProgress(prev => ({ ...prev, post: 0 }));
      const timer = setInterval(() => setProgress(prev => prev.post < 90 ? { ...prev, post: prev.post + 10 } : prev), 100);
      const fd = new FormData();
      fd.append('image', file);
      try {
        const res = await api.upload(`/outreach/posts/${updatedPost._id}/image`, fd);
        updatedPost = res;
        clearInterval(timer);
        setProgress(prev => ({ ...prev, post: 100 }));
      } catch (err) {
        clearInterval(timer);
        setProgress(prev => ({ ...prev, post: 0 }));
        setPostMsg(err.message || 'Error uploading image. Max size is 5MB.');
      }
      await new Promise(r => setTimeout(r, 200));
    }
    setTimeout(() => setProgress(prev => ({ ...prev, post: 0 })), 500);
    setCurrentPost(updatedPost);
  };

  const deletePostImage = async (idx) => {
    try {
      const res = await api.delete(`/outreach/posts/${currentPost._id}/image/${idx}`);
      setCurrentPost(res);
    } catch (err) { setPostMsg(err.message || 'Error deleting image.'); }
  };

  // Section post handlers
  const handleSectionPostCreate = async () => {
    setSaving(true); setSectionPostMsg('');
    try {
      const updated = await api.post(`/outreach/sections/${currentSection._id}/posts`, { title: currentSectionPost.title, description: currentSectionPost.description });
      const newPost = updated.posts[updated.posts.length - 1];
      setCurrentSectionPost(newPost);
      setCurrentSection(updated);
      setSectionPostMsg('Post created. You can now add images.');
    } catch (err) { setSectionPostMsg(err.message || 'Error creating post.'); }
    finally { setSaving(false); }
  };

  const handleSectionPostSave = async () => {
    setSaving(true); setSectionPostMsg('');
    try {
      const updated = await api.put(`/outreach/sections/${currentSection._id}/posts/${currentSectionPost._id}`, { title: currentSectionPost.title, description: currentSectionPost.description });
      setCurrentSection(updated);
      setSectionView('sectionDetail');
    } catch (err) { setSectionPostMsg(err.message || 'Error saving post.'); }
    finally { setSaving(false); }
  };

  const deleteSectionPost = async (sectionId, postId) => {
    if (!window.confirm('Delete?')) return;
    const updated = await api.delete(`/outreach/sections/${sectionId}/posts/${postId}`);
    setCurrentSection(updated);
  };

  const handleSectionPostImage = async (files) => {
    if (!files) return;
    const fileList = Array.isArray(files) ? files : [files];
    let updatedSection = { ...currentSection };
    setSectionPostMsg('');
    for (const file of fileList) {
      setProgress(prev => ({ ...prev, sectionPost: 0 }));
      const timer = setInterval(() => setProgress(prev => prev.sectionPost < 90 ? { ...prev, sectionPost: prev.sectionPost + 10 } : prev), 100);
      const fd = new FormData();
      fd.append('image', file);
      try {
        const res = await api.upload(`/outreach/sections/${updatedSection._id}/posts/${currentSectionPost._id}/image`, fd);
        updatedSection = res;
        const updatedPost = res.posts.find(p => p._id === currentSectionPost._id);
        if (updatedPost) setCurrentSectionPost(updatedPost);
        clearInterval(timer);
        setProgress(prev => ({ ...prev, sectionPost: 100 }));
      } catch (err) {
        clearInterval(timer);
        setProgress(prev => ({ ...prev, sectionPost: 0 }));
        setSectionPostMsg(err.message || 'Error uploading image. Max size is 5MB.');
      }
      await new Promise(r => setTimeout(r, 200));
    }
    setTimeout(() => setProgress(prev => ({ ...prev, sectionPost: 0 })), 500);
    setCurrentSection(updatedSection);
  };

  const deleteSectionPostImage = async (idx) => {
    try {
      const res = await api.delete(`/outreach/sections/${currentSection._id}/posts/${currentSectionPost._id}/image/${idx}`);
      const updatedPost = res.posts.find(p => p._id === currentSectionPost._id);
      if (updatedPost) setCurrentSectionPost(updatedPost);
      setCurrentSection(res);
    } catch (err) { setSectionPostMsg(err.message || 'Error deleting image.'); }
  };

  const handleSectionImage = async (file) => {
    if (!file) return;
    setProgress(prev => ({ ...prev, sectionImg: 0 }));
    const timer = setInterval(() => setProgress(prev => prev.sectionImg < 90 ? { ...prev, sectionImg: prev.sectionImg + 10 } : prev), 100);
    const fd = new FormData(); fd.append('image', file);
    try {
      const res = await api.upload(`/outreach/sections/${currentSection._id}/image`, fd);
      setCurrentSection(res);
      clearInterval(timer);
      setProgress(prev => ({ ...prev, sectionImg: 100 }));
      setTimeout(() => setProgress(prev => ({ ...prev, sectionImg: 0 })), 500);
    } catch (err) {
      clearInterval(timer);
      setProgress(prev => ({ ...prev, sectionImg: 0 }));
      setMsg(err.message || 'Error uploading image.');
    }
  };

  const saveSection = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/outreach/sections/${currentSection._id}`, { title: currentSection.title, description: currentSection.description });
      setCurrentSection(res);
      setMsg('Section saved');
    } catch (err) { setMsg('Error saving section.'); }
    finally { setSaving(false); }
  };

  const deleteSection = async (id) => {
    if (!window.confirm('Delete this section and all its posts?')) return;
    await api.delete(`/outreach/sections/${id}`);
    loadData();
  };

  // Section post view
  if (sectionView === 'sectionPost') {
    const isNew = !currentSectionPost._id;
    return (
      <Container>
        <h2>{isNew ? 'New Section Post' : 'Edit Section Post'}</h2>
        {sectionPostMsg && <div style={{color: sectionPostMsg.startsWith('Post created') ? 'green' : 'red', marginBottom:'1rem'}}>{sectionPostMsg}</div>}
        <Card>
          <CardContent>
            <FormGroup><label>Title</label><Input value={currentSectionPost.title} onChange={e=>{setCurrentSectionPost({...currentSectionPost, title:e.target.value}); setSectionPostMsg('');}} /></FormGroup>
            <FormGroup><label>Description</label><TextArea value={currentSectionPost.description} onChange={e=>{setCurrentSectionPost({...currentSectionPost, description:e.target.value}); setSectionPostMsg('');}} /></FormGroup>
            {isNew ? (
              <Button onClick={handleSectionPostCreate} disabled={saving}>{saving ? 'Creating...' : 'Create Post & Add Images'}</Button>
            ) : (
              <>
                <FormGroup>
                  <label>Images</label>
                  <ImageUploader onImageSelect={handleSectionPostImage} multiple={true} />
                  {progress.sectionPost > 0 && <ProgressBar progress={progress.sectionPost} />}
                  <div style={{display:'flex', gap:'1rem', marginTop:'1rem', flexWrap:'wrap'}}>
                    {currentSectionPost.images?.map((img, i) => (
                      <div key={i} style={{position:'relative', width:'80px'}}>
                        <img src={getImageUrl(img)} alt="" style={{width:'100%', height:'80px', objectFit:'cover', borderRadius:'4px'}} />
                        <button onClick={()=>deleteSectionPostImage(i)} style={{position:'absolute',top:0,right:0,background:'red',color:'white',border:'none',cursor:'pointer',width:'20px',height:'20px',borderRadius:'50%',fontSize:'12px'}}>x</button>
                      </div>
                    ))}
                  </div>
                </FormGroup>
                <Button onClick={handleSectionPostSave} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</Button>
              </>
            )}
            <Button onClick={()=>{setSectionView('sectionDetail'); setSectionPostMsg('');}} $variant="secondary" style={{marginLeft:'1rem'}}>Back</Button>
          </CardContent>
        </Card>
      </Container>
    );
  }

  // Section detail view
  if (sectionView === 'sectionDetail') {
    return (
      <Container>
        <h2>Edit Section</h2>
        {msg && <div style={{color:'green', marginBottom:'1rem'}}>{msg}</div>}
        <Card style={{marginBottom:'2rem'}}>
          <CardContent>
            <FormGroup><label>Section Title</label><Input value={currentSection.title} onChange={e=>setCurrentSection({...currentSection, title:e.target.value})} /></FormGroup>
            <FormGroup><label>Section Description</label><TextArea value={currentSection.description} onChange={e=>setCurrentSection({...currentSection, description:e.target.value})} /></FormGroup>
            <label style={{display:'block', marginBottom:'0.5rem', fontWeight:500}}>Section Image</label>
            <ImageUploader previewUrl={currentSection.image} onImageSelect={handleSectionImage} />
            {progress.sectionImg > 0 && <ProgressBar progress={progress.sectionImg} />}
            <Button onClick={saveSection} style={{marginTop:'1rem'}} disabled={saving}>{saving?'Saving...':'Save Section'}</Button>
            <Button onClick={()=>{setSectionView('list'); setCurrentSection(null); loadData();}} $variant="secondary" style={{marginLeft:'1rem'}}>Back</Button>
          </CardContent>
        </Card>
        <HeaderRow>
          <h3>Section Posts</h3>
          <Button onClick={()=>{setCurrentSectionPost({title:'', description:'', images:[]}); setSectionView('sectionPost');}}>Add Post</Button>
        </HeaderRow>
        <Card>
          {currentSection.posts?.length === 0 && <div style={{padding:'1rem', color:'#888'}}>No posts yet.</div>}
          {currentSection.posts?.map(post => (
            <div key={post._id} style={{padding:'1rem', borderBottom:'1px solid #eee', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
              <div style={{display:'flex', alignItems:'center', gap:'1rem'}}>
                {post.images?.[0] && <img src={getImageUrl(post.images[0])} alt="" style={{width:'50px', height:'50px', objectFit:'cover', borderRadius:'4px'}} />}
                <span>{post.title || <em style={{color:'#888'}}>Untitled</em>}</span>
              </div>
              <div>
                <Button onClick={()=>{setCurrentSectionPost(post); setSectionView('sectionPost');}} $variant="secondary" style={{marginRight:'0.5rem'}}>Edit</Button>
                <Button onClick={()=>deleteSectionPost(currentSection._id, post._id)} $variant="danger">Delete</Button>
              </div>
            </div>
          ))}
        </Card>
      </Container>
    );
  }

  if (view === 'post') {
    const isNew = !currentPost._id;
    return (
      <Container>
        <h2>{isNew ? 'New Post' : 'Edit Post'}</h2>
        {postMsg && <div style={{color: postMsg.startsWith('Post created') ? 'green' : 'red', marginBottom:'1rem'}}>{postMsg}</div>}
        <Card>
          <CardContent>
            <FormGroup><label>Title</label><Input value={currentPost.title} onChange={e=>{setCurrentPost({...currentPost, title:e.target.value}); setPostMsg('');}} /></FormGroup>
            <FormGroup><label>Description</label><TextArea value={currentPost.description} onChange={e=>{setCurrentPost({...currentPost, description:e.target.value}); setPostMsg('');}} /></FormGroup>
            {isNew ? (
              <Button onClick={handlePostCreate} disabled={saving}>{saving ? 'Creating...' : 'Create Post & Add Images'}</Button>
            ) : (
              <>
                <FormGroup>
                  <label>Images</label>
                  <ImageUploader onImageSelect={handlePostImage} multiple={true} />
                  {progress.post > 0 && <ProgressBar progress={progress.post} />}
                  <div style={{display:'flex', gap:'1rem', marginTop:'1rem', flexWrap:'wrap'}}>
                    {currentPost.images?.map((img, i) => (
                      <div key={i} style={{position:'relative', width:'80px'}}>
                        <img src={getImageUrl(img)} alt="" style={{width:'100%', height:'80px', objectFit:'cover', borderRadius:'4px'}} />
                        <button onClick={()=>deletePostImage(i)} style={{position:'absolute',top:0,right:0,background:'red',color:'white',border:'none',cursor:'pointer',width:'20px',height:'20px',borderRadius:'50%',fontSize:'12px'}}>x</button>
                      </div>
                    ))}
                  </div>
                </FormGroup>
                <Button onClick={handlePostSave} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</Button>
              </>
            )}
            <Button onClick={()=>{setView('list'); setPostMsg('');}} $variant="secondary" style={{marginLeft:'1rem'}}>Cancel</Button>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <h2>Manage Outreach</h2>
      {msg && <div style={{color:'green'}}>{msg}</div>}

      <Card style={{marginBottom:'2rem'}}>
        <CardContent>
          <h3>Header</h3>
          <FormGroup><label>Title</label><Input value={header.title} onChange={e=>setHeader({...header, title:e.target.value})} /></FormGroup>
          <FormGroup><label>Description</label><TextArea value={header.description} onChange={e=>setHeader({...header, description:e.target.value})} /></FormGroup>
          <ImageUploader previewUrl={header.image} onImageSelect={handleHeaderImage} />
          {progress.header > 0 && <ProgressBar progress={progress.header} />}
          <Button onClick={saveHeader} style={{marginTop:'1rem'}} disabled={saving}>{saving?'Saving...':'Save Header'}</Button>
        </CardContent>
      </Card>

      <Card style={{marginBottom:'2rem'}}>
        <CardContent>
          <h3>Info Section</h3>
          <FormGroup><label>Title</label><Input value={info.title} onChange={e=>setInfo({...info, title:e.target.value})} /></FormGroup>
          <FormGroup><label>Description</label><TextArea value={info.description} onChange={e=>setInfo({...info, description:e.target.value})} /></FormGroup>
          <ImageUploader previewUrl={info.image} onImageSelect={handleInfoImage} />
          {progress.info > 0 && <ProgressBar progress={progress.info} />}
          <Button onClick={saveInfo} style={{marginTop:'1rem'}} disabled={saving}>{saving?'Saving...':'Save Info'}</Button>
        </CardContent>
      </Card>

      <HeaderRow><h3>Posts</h3><Button onClick={()=>{setCurrentPost({title:'', description:'', images:[]}); setView('post');}}>Add Post</Button></HeaderRow>
      <Card style={{marginBottom:'2rem'}}>
        {posts.length === 0 && <div style={{padding:'1rem', color:'#888'}}>No posts yet.</div>}
        {posts.map(post => (
          <div key={post._id} style={{padding:'1rem', borderBottom:'1px solid #eee', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
            <div style={{display:'flex', alignItems:'center', gap:'1rem'}}>
              {post.images?.[0] && <img src={getImageUrl(post.images[0])} alt="" style={{width:'50px', height:'50px', objectFit:'cover', borderRadius:'4px'}} />}
              <span>{post.title || <em style={{color:'#888'}}>Untitled</em>}</span>
            </div>
            <div>
              <Button onClick={()=>{setCurrentPost(post); setView('post');}} $variant="secondary" style={{marginRight:'0.5rem'}}>Edit</Button>
              <Button onClick={()=>deletePost(post._id)} $variant="danger">Delete</Button>
            </div>
          </div>
        ))}
      </Card>

      <HeaderRow>
        <h3>Sections</h3>
        <Button onClick={async () => {
          const created = await api.post('/outreach/sections', { title: '', description: '' });
          setCurrentSection(created); setSectionView('sectionDetail');
        }}>Add Section</Button>
      </HeaderRow>
      <Card>
        {sections.length === 0 && <div style={{padding:'1rem', color:'#888'}}>No sections yet.</div>}
        {sections.map(section => (
          <div key={section._id} style={{padding:'1rem', borderBottom:'1px solid #eee', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
            <span>{section.title || <em style={{color:'#888'}}>Untitled Section</em>} <span style={{color:'#888', fontSize:'0.85rem'}}>({section.posts?.length || 0} posts)</span></span>
            <div>
              <Button onClick={()=>{setCurrentSection(section); setSectionView('sectionDetail');}} $variant="secondary" style={{marginRight:'0.5rem'}}>Edit</Button>
              <Button onClick={()=>deleteSection(section._id)} $variant="danger">Delete</Button>
            </div>
          </div>
        ))}
      </Card>
    </Container>
  );
};

export default OutreachAdmin;
